import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  ThemeProvider, CssBaseline, useColorScheme, Box, Drawer, Typography, IconButton, Divider,
  List, ListItemButton, ListItemText, Radio, ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { PRESETS, PRIMARY_SWATCHES, buildTheme } from './theme';

const KEY_PRESET = 'atlas.theme.preset';
const KEY_PRIMARY = 'atlas.theme.primary';

interface Ctx { open: () => void }
const CustomizerCtx = createContext<Ctx>({ open: () => {} });
export const useCustomizer = () => useContext(CustomizerCtx);

export function ThemeCustomizerProvider({ children }: { children: React.ReactNode }) {
  const [preset, setPreset] = useState(() => localStorage.getItem(KEY_PRESET) || 'default');
  const [primary, setPrimary] = useState(() => localStorage.getItem(KEY_PRIMARY) || ''); // '' → preset default
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem(KEY_PRESET, preset); }, [preset]);
  useEffect(() => { primary ? localStorage.setItem(KEY_PRIMARY, primary) : localStorage.removeItem(KEY_PRIMARY); }, [primary]);

  const theme = useMemo(() => buildTheme(preset, primary || undefined), [preset, primary]);

  return (
    <ThemeProvider theme={theme} defaultMode="system">
      <CssBaseline enableColorScheme />
      <CustomizerCtx.Provider value={{ open: () => setOpen(true) }}>
        {children}
        <CustomizerDrawer
          open={open} onClose={() => setOpen(false)}
          preset={preset} onPreset={(p) => { setPreset(p); setPrimary(''); }}
          primary={primary} onPrimary={setPrimary}
        />
      </CustomizerCtx.Provider>
    </ThemeProvider>
  );
}

function CustomizerDrawer({
  open, onClose, preset, onPreset, primary, onPrimary,
}: {
  open: boolean; onClose: () => void;
  preset: string; onPreset: (p: string) => void;
  primary: string; onPrimary: (c: string) => void;
}) {
  const { mode, setMode } = useColorScheme();
  const activePrimary = primary || PRESETS.find((p) => p.key === preset)?.primary;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 320 } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Customize</Typography>
          <Typography variant="caption" color="text.secondary">Theme, mode & colour</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary">Mode</Typography>
        <ToggleButtonGroup
          fullWidth exclusive size="small" value={mode ?? 'system'}
          onChange={(_, v) => v && setMode(v)} sx={{ mt: 0.5 }}
        >
          <ToggleButton value="light"><LightModeOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} /> Light</ToggleButton>
          <ToggleButton value="dark"><DarkModeOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} /> Dark</ToggleButton>
          <ToggleButton value="system"><SettingsBrightnessOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} /> System</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider />

      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="overline" color="text.secondary">Theme</Typography>
      </Box>
      <List dense sx={{ pt: 0 }}>
        {PRESETS.map((p) => {
          const selected = p.key === preset;
          return (
            <ListItemButton key={p.key} selected={selected} onClick={() => onPreset(p.key)} sx={{ mx: 1, borderRadius: 1.5 }}>
              <Radio checked={selected} size="small" sx={{ mr: 1, p: 0 }} />
              <ListItemText primary={p.name} primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }} />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: p.primary, border: '1px solid rgba(0,0,0,.1)' }} />
                <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: p.accent, border: '1px solid rgba(0,0,0,.1)' }} />
              </Box>
            </ListItemButton>
          );
        })}
      </List>
      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary">Primary colour</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {PRIMARY_SWATCHES.map((c) => {
            const on = activePrimary?.toLowerCase() === c.toLowerCase();
            return (
              <Tooltip key={c} title={c}>
                <Box
                  onClick={() => onPrimary(c)}
                  sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: c, cursor: 'pointer', display: 'grid', placeItems: 'center', color: '#fff', boxShadow: on ? '0 0 0 2px #fff, 0 0 0 4px currentColor' : 'none', outline: on ? `2px solid ${c}` : 'none' }}
                >
                  {on ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Picking a colour overrides the theme’s primary. Choose a theme to reset it.
        </Typography>
      </Box>
    </Drawer>
  );
}
